'use strict';

import '../css/components/kvbm-consent.scss';

const COOKIE_NAME = 'kvbm_consent';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;
const ROOT_DOMAIN = '.keuzevrijbijmij.nl';
const CONSENT_VERSION = 1;

const DEFAULT_PREFERENCES = {
    version: CONSENT_VERSION,
    necessary: true,
    analytics: false,
    marketing: false,
    updatedAt: null
};

const MARKETING_COOKIE_PATTERNS = [
    /^_fbp$/,
    /^_fbc$/,
    /^pbid$/,
    /^pys_/,
    /^last_pys_/,
    /^pysTrafficSource$/,
    /^sbjs_/
];

const ANALYTICS_COOKIE_PATTERNS = [
    /^_ga$/,
    /^_ga_/,
    /^_gid$/,
    /^_gat/
];

function isLocalHost() {
    return ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
}

function getCookieDomain() {
    if (isLocalHost()) {
        return '';
    }

    if (window.location.hostname === 'keuzevrijbijmij.nl' || window.location.hostname.endsWith('.keuzevrijbijmij.nl')) {
        return ROOT_DOMAIN;
    }

    return '';
}

function getCookie(name) {
    const prefix = `${name}=`;
    const cookies = document.cookie ? document.cookie.split(';') : [];

    for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.indexOf(prefix) === 0) {
            return decodeURIComponent(cookie.substring(prefix.length));
        }
    }

    return null;
}

function setCookie(name, value, maxAge) {
    let cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax`;
    const domain = getCookieDomain();

    if (domain) {
        cookie += `; domain=${domain}`;
    }

    if (window.location.protocol === 'https:') {
        cookie += '; Secure';
    }

    document.cookie = cookie;
}

function expireCookie(name, domain) {
    let cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;

    if (domain) {
        cookie += `; domain=${domain}`;
    }

    if (window.location.protocol === 'https:') {
        cookie += '; Secure';
    }

    document.cookie = cookie;
}

function normalizePreferences(preferences) {
    return {
        version: CONSENT_VERSION,
        necessary: true,
        analytics: !!(preferences && preferences.analytics),
        marketing: !!(preferences && preferences.marketing),
        updatedAt: preferences && preferences.updatedAt ? preferences.updatedAt : new Date().toISOString()
    };
}

function parseStoredPreferences() {
    const raw = getCookie(COOKIE_NAME);

    if (!raw) {
        return null;
    }

    try {
        return normalizePreferences(JSON.parse(raw));
    } catch (error) {
        return null;
    }
}

function migrateCookieYesConsent() {
    const raw = getCookie('cookieyes-consent');

    if (!raw) {
        return null;
    }

    return normalizePreferences({
        analytics: /(?:^|,)analytics:yes(?:,|$)/.test(raw),
        marketing: /(?:^|,)advertisement:yes(?:,|$)/.test(raw)
    });
}

function migrateCraftConsent() {
    const raw = getCookie('cookie_consent');

    if (!raw) {
        return null;
    }

    return normalizePreferences({
        analytics: raw === 'allowed',
        marketing: false
    });
}

function getLegacyOrStoredPreferences() {
    const stored = parseStoredPreferences();

    if (stored) {
        return stored;
    }

    const migrated = migrateCookieYesConsent() || migrateCraftConsent();

    if (migrated) {
        savePreferences(migrated, false);
        return migrated;
    }

    return null;
}

function savePreferences(preferences, dispatch = true) {
    const normalized = normalizePreferences(preferences);
    normalized.updatedAt = new Date().toISOString();
    setCookie(COOKIE_NAME, JSON.stringify(normalized), COOKIE_MAX_AGE);

    if (dispatch) {
        document.dispatchEvent(new CustomEvent('kvbm_consent_update', {
            detail: {
                preferences: normalized
            }
        }));
    }

    return normalized;
}

function cookieNameMatches(name, patterns) {
    return patterns.some((pattern) => pattern.test(name));
}

function deleteMatchingCookies(patterns) {
    const domains = [
        '',
        window.location.hostname,
        `.${window.location.hostname}`,
        'keuzevrijbijmij.nl',
        ROOT_DOMAIN
    ];

    document.cookie.split(';').forEach((item) => {
        const name = item.trim().split('=')[0];

        if (!name || !cookieNameMatches(name, patterns)) {
            return;
        }

        domains.forEach((domain) => expireCookie(name, domain));
    });
}

function createScriptFromBlockedScript(blockedScript) {
    const script = document.createElement('script');

    Array.prototype.slice.call(blockedScript.attributes).forEach((attribute) => {
        if (attribute.name === 'type' || attribute.name === 'id' || attribute.name === 'data-kvbm-consent' || attribute.name === 'data-cookieyes') {
            return;
        }

        script.setAttribute(attribute.name, attribute.value);
    });

    script.type = 'text/javascript';

    if (!blockedScript.src) {
        script.text = blockedScript.text || blockedScript.textContent || '';
    }

    return script;
}

function loadBlockedScriptsFor(category) {
    const selectors = [
        `script[type="text/plain"][data-kvbm-consent="${category}"]`
    ];

    if (category === 'analytics' && !(window.KVBM_TRACKING || {}).googleAnalyticsId) {
        selectors.push('script[type="text/plain"][data-cookieyes="analytics"]');
    }

    if (category === 'marketing') {
        selectors.push('script[type="text/plain"][data-cookieyes="advertisement"]');
    }

    document.querySelectorAll(selectors.join(',')).forEach((blockedScript) => {
        if (blockedScript.dataset.kvbmConsentLoaded === '1') {
            return;
        }

        blockedScript.dataset.kvbmConsentLoaded = '1';
        blockedScript.parentNode.insertBefore(createScriptFromBlockedScript(blockedScript), blockedScript);
    });
}

function loadGoogleAnalytics() {
    const tracking = window.KVBM_TRACKING || {};
    const id = tracking.googleAnalyticsId;

    if (!id || window.kvbmGoogleAnalyticsLoaded) {
        return;
    }

    window.kvbmGoogleAnalyticsLoaded = true;
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function () {
        window.dataLayer.push(arguments);
    };

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
    document.head.appendChild(script);

    window.gtag('js', new Date());
    window.gtag('config', id);
}

function cleanupForPreferences(preferences) {
    if (!preferences.analytics) {
        deleteMatchingCookies(ANALYTICS_COOKIE_PATTERNS);
    }

    if (!preferences.marketing) {
        deleteMatchingCookies(MARKETING_COOKIE_PATTERNS);

        if (window.pys && window.pys.Facebook && typeof window.pys.Facebook.disable === 'function') {
            window.pys.Facebook.disable();
        }
    }
}

function applyPreferences(preferences) {
    if (!preferences) {
        return;
    }

    if (preferences.analytics) {
        loadGoogleAnalytics();
        loadBlockedScriptsFor('analytics');
    }

    if (preferences.marketing) {
        loadBlockedScriptsFor('marketing');
    }

    cleanupForPreferences(preferences);
}

function buildConsentHtml() {
    return `
        <div id="kvbm-consent" class="kvbm-consent" hidden>
            <div class="kvbm-consent__backdrop" data-kvbm-consent-close></div>
            <section class="kvbm-consent__banner" data-kvbm-consent-banner role="dialog" aria-modal="true" aria-labelledby="kvbm-consent-title">
                <h2 id="kvbm-consent-title">Cookies op Keuze Vrij Bij Mij</h2>
                <p>We gebruiken noodzakelijke cookies om de website goed te laten werken. Met jouw toestemming gebruiken we ook statistiekcookies en marketingcookies.</p>
                <div class="kvbm-consent__actions">
                    <button type="button" class="kvbm-consent__button kvbm-consent__button--secondary" data-kvbm-consent-preferences>Voorkeuren instellen</button>
                    <button type="button" class="kvbm-consent__button kvbm-consent__button--secondary" data-kvbm-consent-reject>Alles weigeren</button>
                    <button type="button" class="kvbm-consent__button kvbm-consent__button--primary" data-kvbm-consent-accept>Alles accepteren</button>
                </div>
            </section>
            <section class="kvbm-consent__modal" data-kvbm-consent-modal role="dialog" aria-modal="true" aria-labelledby="kvbm-consent-modal-title" hidden>
                <div class="kvbm-consent__modal-header">
                    <h2 id="kvbm-consent-modal-title">Cookievoorkeuren</h2>
                    <button type="button" class="kvbm-consent__close" data-kvbm-consent-close aria-label="Sluiten">×</button>
                </div>
                <div class="kvbm-consent__modal-body">
                    <p>Je kunt per categorie kiezen welke niet-noodzakelijke cookies je toestaat. Noodzakelijke cookies zijn altijd actief.</p>
                    <div class="kvbm-consent__category">
                        <div>
                            <h3>Noodzakelijk</h3>
                            <p>Nodig om de website veilig en goed te laten functioneren.</p>
                        </div>
                        <span class="kvbm-consent__always">Altijd actief</span>
                    </div>
                    <label class="kvbm-consent__category kvbm-consent__category--toggle">
                        <div>
                            <h3>Statistieken</h3>
                            <p>Met Google Analytics krijgen we inzicht in het gebruik van de website.</p>
                        </div>
                        <input type="checkbox" data-kvbm-consent-toggle="analytics">
                    </label>
                    <label class="kvbm-consent__category kvbm-consent__category--toggle">
                        <div>
                            <h3>Marketing</h3>
                            <p>Met marketingcookies meten we Meta-advertenties en kunnen we relevante advertenties tonen.</p>
                        </div>
                        <input type="checkbox" data-kvbm-consent-toggle="marketing">
                    </label>
                    <p class="kvbm-consent__more"><a href="https://keuzevrijbijmij.nl/privacy-policy">Lees meer in onze privacyverklaring.</a></p>
                </div>
                <div class="kvbm-consent__modal-footer">
                    <button type="button" class="kvbm-consent__button kvbm-consent__button--secondary" data-kvbm-consent-reject>Alles weigeren</button>
                    <button type="button" class="kvbm-consent__button kvbm-consent__button--secondary" data-kvbm-consent-save>Mijn voorkeuren opslaan</button>
                    <button type="button" class="kvbm-consent__button kvbm-consent__button--primary" data-kvbm-consent-accept>Alles accepteren</button>
                </div>
            </section>
        </div>
    `;
}

function ensureConsentElement() {
    let element = document.getElementById('kvbm-consent');

    if (!element) {
        document.body.insertAdjacentHTML('beforeend', buildConsentHtml());
        element = document.getElementById('kvbm-consent');
    }

    return element;
}

function getElements() {
    const root = ensureConsentElement();

    return {
        root,
        banner: root.querySelector('[data-kvbm-consent-banner]'),
        modal: root.querySelector('[data-kvbm-consent-modal]'),
        acceptButtons: root.querySelectorAll('[data-kvbm-consent-accept]'),
        rejectButtons: root.querySelectorAll('[data-kvbm-consent-reject]'),
        preferenceButtons: document.querySelectorAll('[data-kvbm-consent-preferences], .kvbm-cookie-preferences, .cky-banner-element a'),
        saveButton: root.querySelector('[data-kvbm-consent-save]'),
        closeButtons: root.querySelectorAll('[data-kvbm-consent-close]'),
        analyticsToggle: root.querySelector('[data-kvbm-consent-toggle="analytics"]'),
        marketingToggle: root.querySelector('[data-kvbm-consent-toggle="marketing"]')
    };
}

function showRoot(elements) {
    elements.root.hidden = false;
    elements.root.classList.add('kvbm-consent--visible');
}

function hideRoot(elements) {
    elements.root.classList.remove('kvbm-consent--visible');
    elements.root.hidden = true;
    elements.modal.hidden = true;
}

function showBanner(elements) {
    showRoot(elements);
    elements.banner.hidden = false;
    elements.modal.hidden = true;
}

function openPreferences(elements) {
    const preferences = window.kvbmConsent.get() || DEFAULT_PREFERENCES;

    elements.analyticsToggle.checked = !!preferences.analytics;
    elements.marketingToggle.checked = !!preferences.marketing;
    showRoot(elements);
    elements.banner.hidden = true;
    elements.modal.hidden = false;
}

function acceptAll(elements) {
    const preferences = savePreferences({
        analytics: true,
        marketing: true
    });

    applyPreferences(preferences);
    hideRoot(elements);
}

function rejectAll(elements) {
    const preferences = savePreferences({
        analytics: false,
        marketing: false
    });

    applyPreferences(preferences);
    hideRoot(elements);
}

function saveSelected(elements) {
    const preferences = savePreferences({
        analytics: elements.analyticsToggle.checked,
        marketing: elements.marketingToggle.checked
    });

    applyPreferences(preferences);
    hideRoot(elements);
}

function addClickListeners(nodes, handler) {
    Array.prototype.slice.call(nodes).forEach((node) => {
        node.addEventListener('click', (event) => {
            event.preventDefault();
            handler(event);
        });
    });
}

function initializeUi() {
    const elements = getElements();

    addClickListeners(elements.acceptButtons, () => acceptAll(elements));
    addClickListeners(elements.rejectButtons, () => rejectAll(elements));
    addClickListeners(elements.preferenceButtons, () => openPreferences(elements));
    addClickListeners(elements.closeButtons, () => hideRoot(elements));

    if (elements.saveButton) {
        elements.saveButton.addEventListener('click', (event) => {
            event.preventDefault();
            saveSelected(elements);
        });
    }

    if (!window.kvbmConsent.get()) {
        showBanner(elements);
    }
}

function initializeApi(initialPreferences) {
    window.kvbmConsent = {
        get: () => parseStoredPreferences(),
        has: (category) => {
            const preferences = parseStoredPreferences();
            return !!(preferences && preferences[category]);
        },
        set: (preferences) => {
            const saved = savePreferences(preferences);
            applyPreferences(saved);
            return saved;
        },
        openPreferences: () => {
            const elements = getElements();
            openPreferences(elements);
        }
    };

    applyPreferences(initialPreferences);
}

function initialize() {
    const initialPreferences = getLegacyOrStoredPreferences();

    initializeApi(initialPreferences);
    initializeUi();

    document.addEventListener('kvbm_consent_update', (event) => {
        if (event.detail && event.detail.preferences) {
            applyPreferences(event.detail.preferences);
        }
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
} else {
    initialize();
}
